/**
 * Experience: years on a form, months in the column.
 *
 * Recruiters and clients talk in years — "three to five" — and every
 * experience column in the schema stores months. The conversion belongs at the
 * form boundary rather than in either of them: the API, the importer and the
 * seed all speak months, and a column renamed to years would be a migration
 * that bought a label.
 *
 * Its own module so it can be tested. The requirement form is the only caller
 * today, and the rule it encodes — that typing 3 must store 36 — is exactly
 * the kind of thing that is quietly wrong for a year.
 */

/**
 * Whole months for a number of years typed into a form.
 *
 * Blank stays null rather than becoming zero: "no minimum stated" and "no
 * experience required" are different claims, and the column allows both.
 *
 * Rounded, because the column is an integer. Half-years are the only fraction
 * anybody types and 0.5 lands exactly on 6; anything stranger is rounded
 * rather than refused, since a requirement is a description and not an
 * arithmetic exercise.
 */
export function monthsFromYears(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const years = Number(trimmed);
  if (!Number.isFinite(years)) return null;

  return Math.round(years * 12);
}

/**
 * The stored months back as years, for a form that has to show them again.
 *
 * One decimal, and only where it earns it: 18 months reads as 1.5 rather than
 * being floored to 1, which would silently discard six months the moment
 * somebody opened a requirement and saved it. 24 reads as 24, not 24.0.
 *
 * Nothing calls this yet — no screen edits a requirement — and it is written
 * alongside its inverse rather than after the bug, because the two have to
 * agree and the second one is always added in a hurry.
 */
export function yearsFromMonths(months: number | null): string {
  if (months === null) return '';

  const years = months / 12;
  return Number.isInteger(years) ? String(years) : years.toFixed(1);
}
